/**
 * WD Scrolling Icons Banner
 *
 * Drives sections/wd-scrolling-icons-banner.liquid.
 *
 * The CSS does the animating; this element only works out how many copies of the icon
 * group are needed to fill the viewport and how far one copy travels. That distance is
 * handed back to CSS as --wd-sib-distance, so the loop restarts exactly where it began
 * and never visibly jumps, whether the merchant added 2 icons or 30.
 */
(function () {
  'use strict';

  if (customElements.get('wd-scrolling-icons-banner')) return;

  var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  class WdScrollingIconsBanner extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-wd-sib-viewport]');
      this.track = this.querySelector('[data-wd-sib-track]');
      this.group = this.querySelector('[data-wd-sib-group]');

      if (!this.viewport || !this.track || !this.group) return;

      this.root = this.closest('.wd-sib');
      if (!this.root) return;

      this.motionQuery = window.matchMedia(REDUCED_MOTION);
      this.handleMotionChange = this.refresh.bind(this);
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      }

      if (window.ResizeObserver) {
        // Observing the viewport catches window resize; observing the original group
        // catches images finishing decode and changing the measured width. Adding clones
        // changes neither, so this cannot feed back into itself.
        this.resizeObserver = new ResizeObserver(this.refresh.bind(this));
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.group);
      } else {
        this.handleResize = this.refresh.bind(this);
        window.addEventListener('resize', this.handleResize);
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.handleBlockSelect = this.pause.bind(this);
        this.handleBlockDeselect = this.resume.bind(this);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      // A document that is hidden at load (background tab, prerender, bfcache restore,
      // the theme editor's preview iframe before it is shown) suspends rAF indefinitely.
      // Re-check on the way back to visible, and once more after load in case the first
      // pass measured before stylesheets settled.
      this.handleVisibility = function () {
        if (!document.hidden) this.refresh();
      }.bind(this);
      document.addEventListener('visibilitychange', this.handleVisibility);

      if (document.readyState !== 'complete') {
        this.handleLoad = this.refresh.bind(this);
        window.addEventListener('load', this.handleLoad, { once: true });
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.handleResize) window.removeEventListener('resize', this.handleResize);
      if (this.motionQuery && this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionChange);
      }
      if (this.handleBlockSelect) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }
      if (this.handleVisibility) document.removeEventListener('visibilitychange', this.handleVisibility);
      if (this.handleLoad) window.removeEventListener('load', this.handleLoad);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    pause() {
      this.root.classList.add('is-paused');
    }

    resume() {
      this.root.classList.remove('is-paused');
    }

    /**
     * Coalesce bursts of observer callbacks into one layout pass.
     *
     * The very first pass runs synchronously: rAF does not fire in a hidden document,
     * so deferring it would leave the banner as a single un-cloned group with dead
     * space beside it until the tab is focused. Later passes can safely batch.
     */
    refresh() {
      if (!this.initialised) {
        this.layout();
        return;
      }
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.layout.bind(this));
    }

    layout() {
      this.frame = null;

      if (this.dataset.scroll !== 'true' || this.motionQuery.matches) {
        this.setCloneCount(0);
        this.root.classList.remove('is-animating');
        this.root.classList.add('wd-sib--static');
        this.initialised = true;
        return;
      }

      var groupWidth = this.group.getBoundingClientRect().width;
      var viewportWidth = this.viewport.getBoundingClientRect().width;
      // Not measurable yet — stay uninitialised so the next refresh retries synchronously.
      if (groupWidth <= 0 || viewportWidth <= 0) return;

      // The gap between groups is part of the repeat, or the seam would be a pixel short.
      var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      var distance = groupWidth + gap;

      // Two viewports of content guarantees the trailing edge is never on screen mid-loop.
      var copies = Math.max(2, Math.ceil((viewportWidth * 2) / distance) + 1);

      this.root.classList.remove('wd-sib--static');
      this.setCloneCount(copies - 1);

      var speed = parseFloat(this.dataset.speed) || 60;
      if (speed <= 0) speed = 60;

      this.root.style.setProperty('--wd-sib-distance', distance + 'px');
      this.root.style.setProperty('--wd-sib-duration', distance / speed + 's');
      this.root.classList.add('is-animating');
      this.initialised = true;
    }

    setCloneCount(count) {
      var existing = this.track.querySelectorAll('[data-wd-sib-clone]');
      if (existing.length === count) return;

      for (var i = 0; i < existing.length; i++) existing[i].remove();
      if (count <= 0) return;

      var fragment = document.createDocumentFragment();
      for (var c = 0; c < count; c++) fragment.appendChild(this.buildClone());
      this.track.appendChild(fragment);
    }

    buildClone() {
      var clone = this.group.cloneNode(true);

      clone.removeAttribute('data-wd-sib-group');
      clone.setAttribute('data-wd-sib-clone', '');
      clone.setAttribute('aria-hidden', 'true');

      // aria-hidden alone still leaves the copies in the tab order, so links are
      // explicitly removed from it.
      var links = clone.querySelectorAll('a');
      for (var i = 0; i < links.length; i++) links[i].setAttribute('tabindex', '-1');

      // Strip the theme editor's block handles so only the real blocks stay selectable.
      var editable = clone.querySelectorAll('[data-shopify-editor-block]');
      for (var e = 0; e < editable.length; e++) editable[e].removeAttribute('data-shopify-editor-block');

      return clone;
    }
  }

  customElements.define('wd-scrolling-icons-banner', WdScrollingIconsBanner);
})();
